import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Users, Target, CheckCircle, XCircle, Lightbulb } from 'lucide-react';
import { getMethodById } from '../../data/prioritization-selector/methods';

const MethodDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const method = id ? getMethodById(id) : null;

  if (!method) {
    return (
      <div className="text-center">
        <h2 className="text-2xl font-display font-bold text-[var(--text-pri)] mb-4">
          Method Not Found
        </h2>
        <p className="text-[var(--text-sec)] mb-6">
          The requested prioritization method could not be found.
        </p>
        <button
          onClick={() => navigate('..')}
          className="btn-primary"
        >
          Go Back
        </button>
      </div>
    );
  }

  const getComplexityColor = (complexity: string) => {
    switch (complexity) {
      case 'simple':
        return 'text-[var(--green)] dark:text-[var(--text-ter)] bg-[var(--green-dim)]';
      case 'moderate':
        return 'text-[var(--mustard)] bg-[var(--mustard-dim)]';
      case 'complex':
        return 'text-[var(--coral)] bg-[var(--coral-dim)]';
      default:
        return 'text-[var(--text-sec)] bg-[var(--hover-tint)]';
    }
  };

  const getTimeIcon = (timeRequired: string) => {
    switch (timeRequired) {
      case 'quick':
        return <Clock className="w-4 h-4" />;
      case 'moderate':
        return <Clock className="w-4 h-4" />;
      case 'extensive':
        return <Clock className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const getTeamSizeIcon = (teamSize: string) => {
    switch (teamSize) {
      case 'individual':
        return <Target className="w-4 h-4" />;
      case 'small':
        return <Users className="w-4 h-4" />;
      case 'medium':
        return <Users className="w-4 h-4" />;
      case 'large':
        return <Users className="w-4 h-4" />;
      default:
        return <Users className="w-4 h-4" />;
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <button
          onClick={() => navigate('..')}
          className="flex items-center text-[var(--text-sec)] hover:text-[var(--text-pri)] font-medium mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Decision Tree
        </button>
        
        <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-3xl font-display font-bold text-[var(--text-pri)] mb-2">
                {method.name}
              </h1>
              <p className="text-xl text-[var(--text-sec)] mb-4">
                {method.description}
              </p>
              <div className="flex items-center space-x-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getComplexityColor(method.complexity)}`}>
                  {method.complexity} complexity
                </span>
                <span className="flex items-center text-sm text-[var(--text-sec)]">
                  {getTimeIcon(method.timeRequired)}
                  <span className="ml-1">{method.timeRequired} time</span>
                </span>
                <span className="flex items-center text-sm text-[var(--text-sec)]">
                  {getTeamSizeIcon(method.teamSize)}
                  <span className="ml-1">{method.teamSize} team</span>
                </span>
                <span className="text-sm text-[var(--text-sec)] capitalize">
                  {method.category}
                </span>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-display font-bold text-[var(--text-pri)] mb-4">
              Use Cases
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {method.useCases.map((useCase, index) => (
                <div key={index} className="flex items-center p-3 bg-[var(--surface)]">
                  <CheckCircle className="w-5 h-5 text-[var(--green)] mr-3 flex-shrink-0" />
                  <span className="text-[var(--text-sec)]">{useCase}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-display font-bold text-[var(--text-pri)] mb-4">
              When to Use
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {method.whenToUse.map((use, index) => (
                <div key={index} className="flex items-center p-3 bg-[var(--green-dim)]">
                  <CheckCircle className="w-5 h-5 text-[var(--green)] mr-3 flex-shrink-0" />
                  <span className="text-[var(--green)] dark:text-[var(--text-ter)]">{use}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-display font-bold text-[var(--text-pri)] mb-4">
              When NOT to Use
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {method.whenNotToUse.map((notUse, index) => (
                <div key={index} className="flex items-center p-3 bg-[var(--coral-dim)]">
                  <XCircle className="w-5 h-5 text-[var(--coral)] mr-3 flex-shrink-0" />
                  <span className="text-[var(--coral)]">{notUse}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-display font-bold text-[var(--text-pri)] mb-4">
              Step-by-Step Process
            </h2>
            <ol className="space-y-4">
              {method.steps.map((step, index) => (
                <li key={index} className="flex items-start">
                  <span className="flex-shrink-0 w-8 h-8 bg-[var(--slate-dim)] text-[var(--text-sec)] rounded-full flex items-center justify-center text-sm font-semibold mr-4 mt-0.5">
                    {index + 1}
                  </span>
                  <span className="text-[var(--text-sec)] text-lg">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-display font-bold text-[var(--text-pri)] mb-4">
              Pros and Cons
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-[var(--green)] dark:text-[var(--text-ter)] mb-3 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Pros
                </h3>
                <ul className="space-y-2">
                  {method.pros.map((pro, index) => (
                    <li key={index} className="flex items-start">
                      <span className="w-2 h-2 bg-[var(--green)] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span className="text-[var(--text-sec)]">{pro}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[var(--coral)] mb-3 flex items-center">
                  <XCircle className="w-5 h-5 mr-2" />
                  Cons
                </h3>
                <ul className="space-y-2">
                  {method.cons.map((con, index) => (
                    <li key={index} className="flex items-start">
                      <span className="w-2 h-2 bg-[var(--coral)] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                      <span className="text-[var(--text-sec)]">{con}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-display font-bold text-[var(--text-pri)] mb-4">
              Tools and Resources
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {method.tools.map((tool, index) => (
                <div key={index} className="flex items-center p-3 bg-[var(--surface)]">
                  <Lightbulb className="w-5 h-5 text-[var(--text-ter)] mr-3 flex-shrink-0" />
                  <span className="text-[var(--text-sec)]">{tool}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-display font-bold text-[var(--text-pri)] mb-4">
              Examples
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {method.examples.map((example, index) => (
                <div key={index} className="flex items-center p-3 bg-[var(--slate-dim)]">
                  <Target className="w-5 h-5 text-[var(--text-ter)] mr-3 flex-shrink-0" />
                  <span className="text-[var(--text-sec)]">{example}</span>
                </div>
              ))}
            </div>
          </div>

          {method.relatedMethods.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-display font-bold text-[var(--text-pri)] mb-4">
                Related Methods
              </h2>
              <div className="flex flex-wrap gap-2">
                {method.relatedMethods.map((relatedMethod, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-[var(--hover-tint)] text-[var(--text-sec)] rounded-full text-sm hover:bg-[var(--hover-tint)] cursor-pointer"
                  >
                    {relatedMethod}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="text-center">
        <button
          onClick={() => navigate('..')}
          className="btn-primary"
        >
          Start Your Analysis
        </button>
      </div>
    </div>
  );
};

export default MethodDetails;

